import { Sidebar } from 'primereact/sidebar';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../components/inputs/LoadingButton';
import PoliciesForm from './PoliciesForm';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { postPolicy } from '../../../services/Insurance/Policy';
import { useCompanyContext } from '../../../context/CompanyContext';
import { PolicyForm  } from '../../../Interfaces/Insurance/Policy';
import { usePolicySummaryContext } from './PolicyContext';
import { PolicyFiltersOptions } from './PolicyFilters';
import { Status } from '../../../Interfaces/system/Generic.interfaces';

type NewPolicyDrilldownProps = {
  visible: boolean;
  setVisible: (value: boolean) => void;
};

const NewPolicyDrilldown = ({
  visible,
  setVisible,
}: NewPolicyDrilldownProps) => {
  const { selectedCompany } = useCompanyContext();
  const { addOneAtStart } = usePolicySummaryContext();
  const status = useWatch<PolicyFiltersOptions>({ name: 'policyStatus' });
  const formMethods = useForm<PolicyForm>();
  const { handleSubmit, reset } = formMethods;
  const toast = useRef<Toast>(null);

  const createPolicy = useMutation({
    mutationFn: (data: PolicyForm) => {
      return postPolicy(selectedCompany!, data);
    },
    onSuccess: (policy) => {
      reset();
      const { attachmentPath, notes, ...otherFields } = policy;
      const currentDate = new Date();
      const isActive =
        status === Status.active &&
        new Date(policy.termStart).getTime() <= currentDate.getTime() &&
        currentDate.getTime() <= new Date(policy.termEnd).getTime();
      const isInactive =
        status === Status.inactive &&
        currentDate.getTime() > new Date(policy.termEnd).getTime();

      if (isActive || isInactive || status === Status.both) {
        addOneAtStart(otherFields);
      }
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Policy Creation Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: PolicyForm) => {
    createPolicy.mutate(data);
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={() => {
        setVisible(false);
        reset();
        createPolicy.reset();
      }}
      className="w-full max-w-600px"
    >
      <div className="blackText text-standard printBackground max-w-800px">
        <div className="flex flex-column row-gap-4 sm:row-gap-2 sm:flex-row flex-wrap align-items-center justify-content-center sm:justify-content-end">
          <div className="text-28px font-bold mx-auto sm:ml-4 drilldownHeader">
            New Entry
          </div>
        </div>
        <div className="mt-5 ml-4">
          <FormProvider {...formMethods}>
            {!createPolicy.isSuccess && (
              <form className="w-full mx-auto flex flex-column gap-5">
                <PoliciesForm />
                <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
                  <ConfirmationDialog
                    Button={
                      <LoadingButton
                        isLoading={createPolicy.isPending}
                        label="Create"
                        className="w-8rem bluwaiBlue border-0 flex m-auto py-3"
                        fontSize="text-20px"
                        type="button"
                      />
                    }
                    onConfirm={handleSubmit(onSubmit)}
                    message="Are you sure you want to create a new Policy?"
                  />
                </div>
              </form>
            )}
            {createPolicy.isSuccess && (
              <div className="w-full mx-auto flex flex-column gap-5">
                <div className="w-fit mx-auto flex flex-column">
                  <div className="text-center text-40px font-bold text-green-500 mb-3">
                    Success!
                  </div>
                  <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
                  <div className="text-standard mt-3 mx-auto">
                    The Policy has been created successfully!
                  </div>
                </div>
              </div>
            )}
          </FormProvider>
          <ErrorToast toastRef={toast} />
        </div>
      </div>
    </Sidebar>
  );
};

export default NewPolicyDrilldown;
